import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { CtaPrimary } from "@/components/CTA";
import { NAV_LINKS, MEGA } from "@/lib/site";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenMega(null); }, [location.pathname, location.hash]);

  const handleNav = (e, to) => {
    setMobileOpen(false);
    setOpenMega(null);
    if (to && to.includes("#")) {
      const [path, id] = to.split("#");
      if (location.pathname === (path || "/")) {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[100] transition-colors duration-300"
      style={{
        background: scrolled ? "rgba(250,250,248,0.85)" : "rgba(250,250,248,0.0)",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(26,27,24,0.10)" : "1px solid transparent",
      }}
      data-testid="navbar"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-[76px]">
          <Link to="/" data-testid="nav-home-logo" className="flex items-center shrink-0"><Logo /></Link>

          <nav className="hidden lg:flex items-center gap-1" onMouseLeave={() => setOpenMega(null)}>
            {NAV_LINKS.map((l) => (
              <div key={l.label} onMouseEnter={() => setOpenMega(l.mega || null)} className="relative">
                {l.to ? (
                  <Link
                    to={l.to}
                    onClick={(e) => handleNav(e, l.to)}
                    className="px-4 py-2 font-mono-tech text-[0.7rem] uppercase tracking-[0.18em] text-mutedgray hover:text-offwhite transition-colors block"
                    data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <>
                    <button
                      type="button"
                      className="px-4 py-2 font-mono-tech text-[0.7rem] uppercase tracking-[0.18em] text-mutedgray hover:text-offwhite transition-colors inline-flex items-center gap-1.5"
                      data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {l.label}
                      <ChevronDown className={`w-3 h-3 transition-transform ${openMega === l.mega ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {openMega === l.mega && MEGA[l.mega] && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 6 }}
                          transition={{ duration: 0.18 }}
                          className="absolute left-2 top-full pt-2 w-60 z-50"
                          data-testid={`nav-dropdown-${l.mega}`}
                        >
                          <div className="bg-coal border border-offwhite/15 shadow-2xl shadow-black/10 py-2">
                            {MEGA[l.mega].items.map((it) => (
                              <Link
                                key={it.label}
                                to={it.to}
                                onClick={() => setOpenMega(null)}
                                className="flex items-center justify-between px-5 py-3 font-mono-tech text-[0.72rem] uppercase tracking-[0.14em] text-offwhite/80 hover:text-offwhite hover:bg-nearblack transition-colors"
                                data-testid={`nav-dropdown-item-${it.label.toLowerCase()}`}
                              >
                                {it.label}
                                <span className="text-strike">→</span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:block">
            <CtaPrimary to="/quote" testId="nav-request-quote">Request a Quote</CtaPrimary>
          </div>

          <button
            className="lg:hidden text-offwhite p-2"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="nav-mobile-open"
          >
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </div>

      {/* Mega menu */}
      <AnimatePresence>
        {openMega && openMega !== "industries" && MEGA[openMega] && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
            className="hidden lg:block absolute left-0 right-0 top-[76px] bg-coal/95 backdrop-blur-2xl border-y border-offwhite/10"
            onMouseEnter={() => setOpenMega(openMega)}
            onMouseLeave={() => setOpenMega(null)}
            data-testid={`mega-${openMega}`}
          >
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-12 gap-8">
              <div className="col-span-3">
                <p className="tech-label text-strike mb-4">{MEGA[openMega].title}</p>
                <p className="font-display text-2xl font-bold text-offwhite leading-tight">
                  {openMega === "parts" && "Mining equipment spare & wear parts."}
                  {openMega === "industries" && "Built for operations that never stop."}
                  {openMega === "uptime" && "Programs built around downtime math."}
                </p>
              </div>
              <div className="col-span-9 grid grid-cols-3 gap-x-6 gap-y-1">
                {MEGA[openMega].items.map((it, i) => (
                  <Link
                    key={it.label + i}
                    to={it.to}
                    className="group flex items-center justify-between border-b border-offwhite/8 py-4 hover:border-strike transition-colors"
                    data-testid={`mega-item-${i}`}
                  >
                    <span className="font-display text-lg text-offwhite/80 group-hover:text-offwhite group-hover:translate-x-1 transition-transform">{it.label}</span>
                    <span className="text-mutedgray group-hover:text-strike transition-colors">→</span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile fullscreen */}
      {createPortal(
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] bg-coal lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between h-[76px] px-6 border-b border-offwhite/10">
              <Logo />
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="text-offwhite p-2" data-testid="nav-mobile-close">
                <X className="w-7 h-7" />
              </button>
            </div>
            <div className="px-6 py-8">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  {l.to ? (
                    <Link to={l.to} onClick={(e) => handleNav(e, l.to)} className="flex items-center gap-4 py-4 border-b border-offwhite/8 font-display text-xl font-semibold text-offwhite/90 hover:text-offwhite transition-colors" data-testid={`mnav-${i}`}>
                      <span className="font-mono-tech text-[0.65rem] text-strike tracking-widest">0{i + 1}</span>{l.label}
                    </Link>
                  ) : (
                    <div className="py-4 border-b border-offwhite/8" data-testid={`mnav-${i}`}>
                      <div className="flex items-center gap-4 font-display text-xl font-semibold text-offwhite/90">
                        <span className="font-mono-tech text-[0.65rem] text-strike tracking-widest">0{i + 1}</span>{l.label}
                      </div>
                      {l.mega && MEGA[l.mega] && (
                        <div className="mt-3 ml-9 flex flex-col gap-2">
                          {MEGA[l.mega].items.map((it) => (
                            <Link key={it.label} to={it.to} onClick={(e) => handleNav(e, it.to)} className="font-display text-base text-mutedgray hover:text-offwhite transition-colors" data-testid={`mnav-${i}-${it.label.toLowerCase()}`}>
                              {it.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <CtaPrimary to="/quote" testId="mnav-quote" className="w-full justify-center">Request a Quote</CtaPrimary>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body)}
    </header>
  );
}
